import React, { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { DocumentUploadFieldAccountDocumentType } from "common/services/formBuilderService";
import { SUBMISSION_FIELD_TYPES } from "common/utils/formStructure";
import { GetSubmissionTypeForBuilderQuery } from "../../../../../generated/graphql";
import { AuthContextType } from "../../../../Authorization/types";
import { AssistantSuggestion } from "../../../../Submissions/ChatAgent/AssistantIntro";
import ChatPanel from "../../../../Submissions/ChatAgent/ChatPanel";
import ChatToggleButton from "../../../../Submissions/ChatAgent/ChatToggleButton";
import {
  editorTypeOptions,
  editorTypeToFieldPermissionsMap,
  numberDynamicDefaultInputOptions,
  textDynamicDefaultInputOptions,
  viewerTypeOptions,
  viewerTypeToFieldPermissionsMap,
} from "./constants";
import { SubmissionsBuilderFormDataStructure } from "./types";

const UNIQUE_ID_NUMBER_TYPE_OPTIONS = ["ascending", "random"] as const;
const CHECKBOX_DEFAULT_VALUE_PATHS = [null, "boolean.true"] as const;
const NUMBER_PREFIX_OPTIONS = ["$"] as const;
const NUMBER_POSTFIX_OPTIONS = ["%"] as const;
const NUMBER_FORMAT_OPTIONS = ["$", "%"] as const;
const BUILDER_SUGGESTIONS: AssistantSuggestion[] = [
  {
    text: "Draft a new damage assessment record with grouped sections for structure, utilities, and safety notes.",
    iconName: "component",
  },
  {
    text: "Add photo and document upload fields to capture site evidence for this form.",
    iconName: "upload",
  },
  {
    text: "Create dropdowns with standard compliance statuses and required follow-up actions.",
    iconName: "list",
  },
];

const prepareDocumentTypesForAI = (
  account: AuthContextType["account"],
): DocumentUploadFieldAccountDocumentType[] => {
  if (!account?.accountDocumentTypes) {
    return [];
  }

  return account.accountDocumentTypes.map(adt => ({
    id: adt.id,
    name: adt.name,
    systemType: adt.documentType?.name,
    allowedMimeTypes: adt.allowedMimeTypes,
  }));
};

export const useSubmissionsBuilderChatAgent = ({
  account,
  existingSubmissionType,
}: {
  account: AuthContextType["account"];
  existingSubmissionType: GetSubmissionTypeForBuilderQuery["submissionType"];
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const ToggleButton = useMemo(() => {
    return (
      <ChatToggleButton onClick={() => setIsOpen(!isOpen)} isOpen={isOpen} />
    );
  }, [isOpen]);

  const { setValue, watch, trigger } =
    useFormContext<SubmissionsBuilderFormDataStructure>();

  const capabilities = useMemo(
    () => ({
      types: SUBMISSION_FIELD_TYPES,
      defaults: {
        text: {
          static: "defaultValue",
          dynamic: {
            key: "defaultValuePath",
            options: textDynamicDefaultInputOptions,
          },
        },
        textarea: {
          static: {
            keys: ["defaultValue", "useGenerativeAI", "generativeAIPrompt"],
          },
          dynamic: {
            key: "defaultValuePath",
            options: textDynamicDefaultInputOptions,
          },
        },
        number: {
          static: {
            keys: ["defaultValue", "minimum", "maximum"],
            helpers: {
              step: "step",
            },
          },
          dynamic: {
            key: "defaultValuePath",
            options: numberDynamicDefaultInputOptions,
          },
          format: {
            key: "prefixPostfix",
            options: NUMBER_FORMAT_OPTIONS,
            translate: {
              $: { prefix: "$" },
              "%": { postfix: "%" },
            },
          },
        },
        NumberWithUnits: {
          static: {
            keys: ["defaultValue", "unitsDefaultValue"],
          },
          dynamic: {
            key: "defaultValuePath",
            options: numberDynamicDefaultInputOptions,
          },
        },
        select: {
          static: {
            key: "defaultValue",
            mustBeIn: "enum",
          },
        },
        multiSelect: {
          static: {
            key: "defaultValue",
            type: "array<string>",
            mustBeIn: "enum",
          },
        },
        Tags: {
          static: {
            key: "defaultValue",
            type: "array<string>",
            mustBeIn: "enum",
          },
        },
        checkbox: {
          dynamic: {
            key: "defaultValuePath",
            values: CHECKBOX_DEFAULT_VALUE_PATHS,
          },
        },
        date: {
          flags: ["defaultToToday"],
        },
        UniqueId: {
          static: {
            keys: ["prefix"],
          },
          enums: {
            numberType: UNIQUE_ID_NUMBER_TYPE_OPTIONS,
          },
        },
        PropertyMarketValue: {
          staticNested: [
            "overrides.squareFootageEstimate.costPerSqft.defaultValue",
          ],
        },
      },
      permissions: {
        viewerTypes: viewerTypeOptions,
        editorTypes: editorTypeOptions,
        viewerTypeToFieldPermissionsMap,
        editorTypeToFieldPermissionsMap,
      },
      dynamicDefaults: {
        text: textDynamicDefaultInputOptions,
        number: numberDynamicDefaultInputOptions,
      },
      account: {
        userGroups:
          account?.userGroups?.map(({ id, name }) => ({ id, name })) ?? [],
        documentTypes: prepareDocumentTypesForAI(account),
      },
      submissionType: {
        id: existingSubmissionType?.id,
      },
      enums: {
        UniqueId: {
          numberType: UNIQUE_ID_NUMBER_TYPE_OPTIONS,
        },
        number: {
          prefix: NUMBER_PREFIX_OPTIONS,
          postfix: NUMBER_POSTFIX_OPTIONS,
          prefixPostfix: NUMBER_FORMAT_OPTIONS,
        },
      },
      typeConstraints: {
        number: "number",
        NumberWithUnits: "number",
      },
    }),
    [account, existingSubmissionType],
  );

  const watchedInputs = watch("inputs");
  const watchedModules = watch("modules");
  const watchedCanCreate = watch("canCreateRolesAndGroupIds");
  const watchedHidden = watch("hiddenFromPublicByDefault");

  const builderState = useMemo(
    () => ({
      inputs: watchedInputs ?? [],
      modules: watchedModules ?? [],
      canCreateRolesAndGroupIds: watchedCanCreate ?? [],
      hiddenFromPublicByDefault: watchedHidden ?? false,
      __capabilities: capabilities,
    }),
    [
      watchedInputs,
      watchedModules,
      watchedCanCreate,
      watchedHidden,
      capabilities,
    ],
  );

  const Panel = useMemo(() => {
    return (
      <ChatPanel
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        submissionTypeVersionId={undefined}
        formData={builderState}
        api="/api/ai/chat/submissions-builder/stream"
        suggestions={BUILDER_SUGGESTIONS}
        onFormUpdate={(out: any) => {
          const next = out?.builderState;
          if (!next) return;

          if (Array.isArray(next.inputs)) {
            setValue("inputs", next.inputs, { shouldDirty: true });
          }
          if ("canCreateRolesAndGroupIds" in next)
            setValue(
              "canCreateRolesAndGroupIds",
              next.canCreateRolesAndGroupIds,
              {
                shouldDirty: true,
              },
            );
          if ("hiddenFromPublicByDefault" in next)
            setValue(
              "hiddenFromPublicByDefault",
              next.hiddenFromPublicByDefault,
              { shouldDirty: true },
            );

          void trigger();
        }}
      />
    );
  }, [isOpen, builderState, setValue, trigger]);

  return {
    isOpen,
    setIsOpen,
    ChatToggleButton: ToggleButton,
    ChatPanel: Panel,
    isChatPanelOpen: isOpen,
  } as const;
};

export default useSubmissionsBuilderChatAgent;
